import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  schema?: object;
}

export const useSEO = ({ title, description, keywords, canonical, schema }: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', `${siteUrl}${location.pathname}`, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:site_name', 'The Villarreal Health Network', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical || `${siteUrl}${location.pathname}`);

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, schema, location.pathname, siteUrl]);
};

export const getOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "The Villarreal Health Network",
    "url": siteUrl,
    "logo": `${siteUrl}/vite.svg`,
    "description": "Comprehensive home healthcare network connecting families with trusted providers in Las Vegas, Nevada",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "3755 Breakthrough Way",
      "addressLocality": "Las Vegas",
      "addressRegion": "NV",
      "postalCode": "89135",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "36.1699",
      "longitude": "-115.1398"
    },
    "telephone": "+1-702-555-1234",
    "email": "info@villarrealhealth.com",
    "areaServed": {
      "@type": "State",
      "name": "Nevada"
    },
    "medicalSpecialty": [
      "Home Health Care",
      "Skilled Nursing",
      "Personal Care",
      "Memory Care",
      "Physical Therapy"
    ]
  };
};

export const getLocalBusinessSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "The Villarreal Health Network",
    "image": `${siteUrl}/vite.svg`,
    "url": siteUrl,
    "telephone": "+1-702-555-1234",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "3755 Breakthrough Way",
      "addressLocality": "Las Vegas",
      "addressRegion": "NV",
      "postalCode": "89135",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "36.1699",
      "longitude": "-115.1398"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "14:00"
      }
    ]
  };
};

export const getWebsiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "The Villarreal Health Network",
    "url": siteUrl,
    "description": "Trusted home healthcare network connecting families with specialized providers in Las Vegas, Nevada",
    "publisher": {
      "@type": "MedicalOrganization",
      "name": "The Villarreal Health Network",
      "url": siteUrl
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/compare?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};

export const getFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const getServiceSchema = (serviceName: string, description: string) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": serviceName,
    "description": description,
    "provider": {
      "@type": "MedicalOrganization",
      "name": "The Villarreal Health Network",
      "url": siteUrl
    },
    "areaServed": {
      "@type": "State",
      "name": "Nevada"
    }
  };
};

export const getBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${siteUrl}${item.url}`
    }))
  };
};
